/**
 * data/loader.ts — load the pre-generated JSON produced by convert-xlsx.ts
 * and adapt it into runtime shapes (ShipTemplate, etc.).
 *
 * JSON is imported statically via Vite's JSON loader so it's bundled into the
 * main chunk — no runtime fetch needed.
 */

import shipsJson from './ships.json';
import techJson from './tech-tree.json';
import habitabilityJson from './habitability.json';
import aiBehavioursJson from './ai-behaviours.json';
import structuresJson from './structures.json';
import balanceJson from './balance.json';
import namesJson from './names.json';
import { STATIONARY_SPECIAL_ABILITY } from '../game/balance-space-structures';
import {
  SQUADRON_CHASSIS_BY_RACE,
  SQUADRON_FIGHTER_COUNT,
  SQUADRON_PARENT_FIGHTER_BY_RACE,
} from '../game/balance-carrier-squadrons';
import type {
  AiBehavioursData,
  AuraDef,
  BalanceData,
  HabitabilityData,
  NamesData,
  RaceId,
  ShipClass,
  ShipTemplate,
  StructuresData,
  TechDef,
  WeaponDef,
} from '../state/types';

interface ShipsJsonShape {
  schemaVersion: number;
  generatedAt: string;
  ships: Array<{
    chassis: string;
    classLevel: number;
    raceId: string;
    role: string;
    hull: number;
    armor: number;
    shields: number;
    empProtection: number;
    sublightSpeed: number;
    dodge: number;
    visionRange: number;
    scanRange: number;
    detectionBase: number;
    stealthBase: number;
    energyCapacity: number | null;
    energyRegen: number | null;
    /**
     * v4M5.1 — three new decomposed hyperdrive stats per
     * `docs/HYPERSPACE.md §1`. Required; older jsons fail validation.
     * v4M5.2b — legacy `hyperdriveFuelUse` field retired.
     */
    hyperdriveSpeed: number;
    hyperdriveRange: number;
    hyperdriveFuelUsePerUnit: number;
    /** Optional in the JSON for now — loader defaults to JUMP_DURATION_DEFAULT. */
    jumpDurationTicks?: number | null;
    weaponLoadout: Array<{ weapon: string; count: number }>;
    embarks: Array<{ chassis: string; count: number }>;
    popBuildCost: number;
    creditsBuildCost: number;
    mineralsBuildCost: number;
    upkeep: number;
    canCloak: boolean;
    specialAbilities: string[];
    /** MFix8.5 — present in regenerated jsons; absent in older blobs. */
    aura?: string | null;
    notes: string | null;
  }>;
  weapons: Array<{
    name: string;
    category: string;
    subType: string;
    mk: number;
    rateOfFire: number;
    range: number;
    damageType: string;
    damage: number;
    accuracy: number;
    magazine: number | null;
    blastRadius: number | null;
    dps: number | null;
    notes: string | null;
  }>;
  races: Array<{
    raceId: string;
    name: string;
    status: 'v1' | 'v2';
    description: string;
    characteristics: string[];
    /**
     * v3M21.5 — multiplier applied to a behaviour's
     * `diplomaticAggression` to produce the empire's effective
     * aggression (`DIPLOMACY.md §9`). RACE-AA (Terran Consortium —
     * "should focus on diplomacy") authored at 1.5×; RACE-BB (Kargath
     * warriors) at 0.7×; RACE-CC (Reman stealth) and v2 placeholders
     * default to 1.0×. The synthetic neutral empire's race ('Neutral')
     * is not in this table — readers must default to 1.0 on miss.
     */
    diplomaticAggressionMultiplier: number;
  }>;
  /** MFix8.5 — present in regenerated jsons; absent in older blobs. */
  auras?: Array<{
    auraName: string;
    range: number;
    shieldMult: number;
    shieldRegenMult: number;
    damageMult: number;
    speedMult: number;
    stealthBonus: number;
    detectionBonus: number;
  }>;
}

interface TechJsonShape {
  schemaVersion: number;
  generatedAt: string;
  techs: Array<{
    techName: string;
    category: string;
    level: number | null;
    race: string;
    prerequisites: string[];
    researchCost: number;
    researchTimeTicks: number | null;
    effect: string;
    unlocks: string | null;
    notes: string | null;
    /** MFix7.2 — present in regenerated jsons; absent in older blobs. */
    parsedMultiplier?: number | null;
    parsedAdditive?: number | null;
    parsedRegenPercent?: number | null;
  }>;
}

export interface LoadedGameData {
  shipTemplates: ReadonlyMap<string, ShipTemplate>;
  /** MFix8.5 — Aura definitions from the Auras sheet on Ships.xlsx. */
  auras: ReadonlyMap<string, AuraDef>;
  /**
   * Legacy raw weapon rows from ships.json. New callers should prefer
   * `weaponDefs` (keyed by name, typed as WeaponDef) so they can look a weapon
   * up in O(1) without rescanning the array. Retained so older renderer /
   * tooltip paths don't break — will be removed once nothing reads it.
   */
  weapons: ShipsJsonShape['weapons'];
  /**
   * Name-keyed weapon catalogue, the shape `GameState.weapons` holds at
   * runtime. `WeaponDef.name` is the lookup key (`'Pulse Laser Mk 1'`,
   * `'Phaser Mk 3'`, etc.). Ship loadouts reference weapons by slot, not by
   * name, so the combat pass uses this map alongside a slot-to-category
   * table (see src/game/combat-balance.ts) to resolve the Mk.
   */
  weaponDefs: ReadonlyMap<string, WeaponDef>;
  races: ShipsJsonShape['races'];
  /** Legacy raw tech rows from tech-tree.json. Prefer `techDefs` for lookups. */
  techs: TechJsonShape['techs'];
  /**
   * Name-keyed tech catalogue, the shape `GameState.techs` holds at runtime.
   * Keyed by `techName`. M12 research pipeline reads cost, prereqs, race
   * gate from here; later effect helpers read category + level.
   */
  techDefs: ReadonlyMap<string, TechDef>;
  /**
   * MFix3.2 — Habitability catalogue from `docs/Habitability.xlsx`. Read by
   * `balance-habitability.ts` to compute per-race per-planet Habitability
   * scores and per-band population dynamics.
   */
  habitability: HabitabilityData;
  /**
   * v3M7.1 — AI behaviours catalogue from `docs/AI_Behaviours.xlsx`. Read
   * by `balance-ai.ts` `getAiConfig(race, difficulty, behaviour)` to
   * resolve a per-empire AiRaceConfig at runtime.
   */
  aiBehaviours: AiBehavioursData;
  /**
   * v3M14a — Structure tunables catalogue from `docs/Structures.xlsx`
   * (currently sourced directly from `src/data/structures.json` until
   * the xlsx is authored). Per-kind cost / hull / max-level / race
   * overrides plus empire-wide flat caps and the Embassy diplomacy
   * curve. Threaded into `state.structures`; consumer migration in
   * `balance-structures.ts` lands in v3M14b.
   */
  structures: StructuresData;
  /**
   * v3M10.1 — cross-cutting balance constants from `docs/Balance.xlsx`
   * → `src/data/balance.json`. Loaded statically; threaded into
   * `state.balanceData` at scenario init and re-attached on save load.
   */
  balance: BalanceData;
  /**
   * v3M20 — name pools from `docs/Names.xlsx` → `src/data/names.json`.
   * Consumed once at scenario init by `pickSystemName` /
   * `pickPlanetName` to assign per-entity flavour names; the picked
   * names persist on `Planet.name` / `StarSystem.name` so this
   * catalogue lives on `LoadedGameData` only and is never re-read at
   * runtime.
   */
  names: NamesData;
}

interface HabitabilityJsonShape {
  schemaVersion: number;
  generatedAt: string;
  raceAffinity: Record<string, Record<string, number>>;
  baseInherent: Record<string, number>;
  techBonus: Array<{
    techName: string;
    techType: string;
    bonuses: Record<string, Record<string, number>>;
  }>;
  bands: HabitabilityData['bands'];
  sizeModulation: Record<string, number>;
}

interface AiBehavioursJsonShape {
  schemaVersion: number;
  generatedAt: string;
  behaviours: AiBehavioursData['behaviours'];
  difficulties: AiBehavioursData['difficulties'];
  structurePriorityByBehaviour: AiBehavioursData['structurePriorityByBehaviour'];
  shipPriorityByRaceBehaviour: AiBehavioursData['shipPriorityByRaceBehaviour'];
  researchPriorityByBehaviour: AiBehavioursData['researchPriorityByBehaviour'];
  spaceStructurePriorityByBehaviour: AiBehavioursData['spaceStructurePriorityByBehaviour'];
}

interface StructuresJsonShape {
  schemaVersion: number;
  generatedAt: string;
  byKind: StructuresData['byKind'];
  /** v3M14c — Constructor-built space structures, keyed by SPACE_STRUCTURE_KINDS. */
  spaceByKind: StructuresData['spaceByKind'];
  /** v3M14c — feature-specific tunables (ODP / Minefield / Warp Gate / LRSA / Repair Yard / extractors). */
  features: StructuresData['features'];
  autoRepairProportionPerTick: number;
  baseCredits: number;
  baseMinerals: number;
  baseFuel: number;
  diplomaticCap: number;
  // v3M27 — storageCapPerLevel + embassyDiplomacyByLevel migrated into
  // per-row `effects` arrays; both top-level fields removed from the
  // JSON schema.
}

interface NamesJsonShape {
  schemaVersion: number;
  generatedAt: string;
  systems: string[];
  planets: string[];
}

/**
 * Default hyperdrive spool ticks when ships.xlsx has no value (10s at 10Hz).
 * Temporary — once the xlsx column is populated per chassis, this only
 * protects against missing/empty cells.
 */
export const JUMP_DURATION_DEFAULT = 100;

/**
 * Look up the human-readable race name for a RaceId.
 *
 * Per CLAUDE.md design decision, display names come from Ships.xlsx → Races
 * sheet (convert-xlsx.ts → ships.json.races). The `RACE-AA` / `RACE-BB` etc.
 * IDs exist so renames in the spreadsheet don't break code; anything
 * player-visible should resolve through this helper.
 *
 * Throws if the race isn't in the loaded catalogue — caller must ensure
 * they're only asking for known races. For safe-fallback display, use
 * {@link getRaceNameOr}.
 */
export function getRaceName(data: LoadedGameData, raceId: RaceId): string {
  const row = data.races.find((r) => r.raceId === raceId);
  if (!row) throw new Error(`Unknown raceId: ${raceId}`);
  return row.name;
}

/**
 * Same as {@link getRaceName} but returns the raceId as a fallback if the
 * race is unknown. Use when rendering possibly-unknown race references
 * (e.g. v2 races in v1 builds).
 */
export function getRaceNameOr(
  data: LoadedGameData,
  raceId: RaceId,
  fallback?: string,
): string {
  const row = data.races.find((r) => r.raceId === raceId);
  return row?.name ?? fallback ?? raceId;
}

/**
 * Inject synthetic ShipTemplates for M14 space structures (STRUCTURES §3).
 * One template per (race × kind) — stats are race-neutral in v1 but the
 * per-race key keeps lookups uniform with regular chassis.
 *
 * Callers (applyBuildSpaceStructure, advance-construction) look up
 * `${empire.raceId}:${kind}` just like any ship chassis. Filtering to
 * avoid these showing up in M10's Build Ship menu is enforced at the
 * UI + command level via the SPACE_STRUCTURE_KINDS set in
 * balance-space-structures.ts.
 */
function injectSpaceStructureTemplates(
  shipTemplates: Map<string, ShipTemplate>,
): void {
  const RACES: ReadonlyArray<RaceId> = ['RACE-AA', 'RACE-BB', 'RACE-CC'];
  for (const raceId of RACES) {
    for (const spec of SPACE_STRUCTURE_SPECS) {
      shipTemplates.set(`${raceId}:${spec.chassis}`, {
        chassis: spec.chassis,
        classLevel: 1,
        raceId,
        role: spec.role,
        hull: spec.hull,
        armor: spec.armor,
        shields: spec.shields,
        empProtection: 0,
        sublightSpeed: 0,
        dodge: 0,
        visionRange: spec.visionRange,
        scanRange: spec.scanRange,
        detectionBase: spec.detectionBase,
        stealthBase: 0,
        energyCapacity: null,
        energyRegen: null,
        // Space structures don't jump — all three decomposed hyperdrive
        // fields zeroed. v4M5.2b retired the legacy `hyperdriveFuelUse`.
        hyperdriveSpeed: 0,
        hyperdriveRange: 0,
        hyperdriveFuelUsePerUnit: 0,
        jumpDurationTicks: JUMP_DURATION_DEFAULT,
        weaponLoadout: spec.weaponLoadout,
        embarks: [],
        // Build costs on the template are not used by the M14 build path —
        // that reads SPACE_STRUCTURE_BUILD_COST from balance. Stored here
        // for defensive compatibility with any future code that inspects
        // the template (e.g. save-file schema validation).
        popBuildCost: 5,
        creditsBuildCost: spec.buildCredits,
        mineralsBuildCost: spec.buildMinerals,
        upkeep: 1,
        canCloak: false,
        // MFix5.2 — flag every space-structure template as Stationary so
        // the move/jump appliers can filter them out via specialAbility
        // membership rather than the brittle `sublightSpeed === 0`
        // discriminator (many test fixtures leave that at 0 as a
        // placeholder unrelated to mobility).
        specialAbilities: [STATIONARY_SPECIAL_ABILITY],
        aura: null,
      });
    }
  }
}

/**
 * v3M17.1 — Inject synthetic squadron ShipTemplates, one per v1 race
 * (Corvette Squadron / Fighter Squadron / Infiltrator Squadron). Each
 * squadron template inherits its parent fighter's role / sublight /
 * dodge / stealth / detection / weaponLoadout from the corresponding
 * Class-1 chassis already loaded into `shipTemplates`. Hull and shields
 * are 5× the parent (full-strength squadron of `SQUADRON_FIGHTER_COUNT`
 * fighters); weapon loadout `count` is multiplied by the same factor so
 * a 5-pack fires a 5-shot volley per cooldown cycle. See SHIPS.md §5.1
 * + docs/v3M17-design.md §1.
 *
 * Called AFTER the regular ship templates load (the squadron stats
 * derive from the parent fighter template, which must already be in
 * `shipTemplates`). Skips silently if the parent is missing — defensive
 * against future race additions before their fighter chassis are
 * authored.
 *
 * Squadrons are NOT in `SPACE_STRUCTURE_KINDS`; they are NOT stamped
 * with `STATIONARY_SPECIAL_ABILITY`. They render and move as regular
 * mobile ships while deployed. v3M17.2 wires the launch / recall pass.
 */
function injectSquadronTemplates(
  shipTemplates: Map<string, ShipTemplate>,
): void {
  const RACES: ReadonlyArray<RaceId> = ['RACE-AA', 'RACE-BB', 'RACE-CC'];
  for (const raceId of RACES) {
    const parentChassis = SQUADRON_PARENT_FIGHTER_BY_RACE[raceId];
    const parent = shipTemplates.get(`${raceId}:${parentChassis}`);
    if (!parent) continue;
    const squadronChassis = SQUADRON_CHASSIS_BY_RACE[raceId];
    shipTemplates.set(`${raceId}:${squadronChassis}`, {
      chassis: squadronChassis,
      // Class 1 — squadrons share their parent fighter's class. Not
      // gated independently; carrier construction is the gate.
      classLevel: 1,
      raceId,
      role: `Squadron of ${SQUADRON_FIGHTER_COUNT} ${parentChassis} strike craft launched from a carrier.`,
      // Aggregate hull / shields = 5× parent (full-strength squadron).
      // The runtime variable-count HP pool (liveFighterCount × parent.hull)
      // lands in v3M17.3; v3M17.1 only stamps the template-time max.
      hull: parent.hull * SQUADRON_FIGHTER_COUNT,
      armor: parent.armor,
      shields: parent.shields * SQUADRON_FIGHTER_COUNT,
      empProtection: parent.empProtection,
      sublightSpeed: parent.sublightSpeed,
      dodge: parent.dodge,
      visionRange: parent.visionRange,
      scanRange: parent.scanRange,
      detectionBase: parent.detectionBase,
      stealthBase: parent.stealthBase,
      // Squadrons inherit their parent's energy pool — relevant if the
      // parent fighter has an energy-driven ability. v1's Class-1 strike
      // craft (Corvette / Fighter / Infiltrator) all have null energy
      // capacity and regen, so this is a no-op in practice but keeps
      // the inheritance contract explicit.
      energyCapacity: parent.energyCapacity,
      energyRegen: parent.energyRegen,
      // Squadrons cannot enter hyperspace per the v3M17.0 design lock.
      // Fuel use 0 + a non-zero placeholder jumpDuration (never read).
      // v4M5.2b: all three decomposed hyperdrive stats zeroed (legacy
      // hyperdriveFuelUse retired alongside this commit).
      hyperdriveSpeed: 0,
      hyperdriveRange: 0,
      hyperdriveFuelUsePerUnit: 0,
      jumpDurationTicks: parent.jumpDurationTicks,
      // 5× the parent's per-slot count — a 5-pack fires a 5-shot volley
      // through a single squadron-level cooldown.
      weaponLoadout: parent.weaponLoadout.map((slot) => ({
        weapon: slot.weapon,
        count: slot.count * SQUADRON_FIGHTER_COUNT,
      })),
      embarks: [],
      // Squadrons have no separate build cost — they are spawned by the
      // carrier's launch path, never built through any production menu.
      popBuildCost: 0,
      creditsBuildCost: 0,
      mineralsBuildCost: 0,
      upkeep: 0,
      // v1 simplification: squadrons cannot cloak. RACE-CC's Infiltrator
      // is cloak-capable but launching pre-cloaked squadrons is a v4
      // concern (similar to the cloaked-Constructor case deferred to
      // v4M0).
      canCloak: false,
      specialAbilities: [],
      aura: null,
    });
  }
}

/** Shape of each synthetic space-structure template before per-race fanout. */
interface SpaceStructureSpec {
  chassis: string;
  role: string;
  hull: number;
  armor: number;
  shields: number;
  visionRange: number;
  scanRange: number;
  detectionBase: number;
  weaponLoadout: Array<{ weapon: string; count: number }>;
  buildCredits: number;
  buildMinerals: number;
}

const SPACE_STRUCTURE_SPECS: readonly SpaceStructureSpec[] = [
  {
    chassis: 'Sensor Buoy',
    role: 'Stationary sensor beacon. Massive vision + scan ranges.',
    hull: 500,
    armor: 0.05,
    shields: 100,
    visionRange: 0.6,
    scanRange: 1.0,
    detectionBase: 80,
    weaponLoadout: [],
    buildCredits: 2000,
    buildMinerals: 500,
  },
  {
    chassis: 'Energy Weapons Platform',
    role: 'Stationary Energy defence platform.',
    hull: 1500,
    armor: 0.15,
    shields: 300,
    visionRange: 0.15,
    scanRange: 0.3,
    detectionBase: 20,
    weaponLoadout: [{ weapon: 'Energy Pulse Weapon', count: 2 }],
    buildCredits: 3000,
    buildMinerals: 1500,
  },
  {
    chassis: 'Kinetic Weapons Platform',
    role: 'Stationary Kinetic defence platform.',
    hull: 1500,
    armor: 0.15,
    shields: 300,
    visionRange: 0.15,
    scanRange: 0.3,
    detectionBase: 20,
    weaponLoadout: [{ weapon: 'Kinetic Burst Weapon', count: 2 }],
    buildCredits: 3000,
    buildMinerals: 1500,
  },
  {
    chassis: 'Concussion Weapons Platform',
    role: 'Stationary Concussion missile platform.',
    hull: 1500,
    armor: 0.15,
    shields: 300,
    visionRange: 0.15,
    scanRange: 0.3,
    detectionBase: 20,
    weaponLoadout: [{ weapon: 'Concussion Missile Weapon', count: 2 }],
    buildCredits: 3000,
    buildMinerals: 1500,
  },
  {
    chassis: 'Repair Yard',
    role: 'Hull regen for friendly ships within radius.',
    hull: 2000,
    armor: 0.15,
    shields: 400,
    visionRange: 0.2,
    scanRange: 0.4,
    detectionBase: 20,
    weaponLoadout: [],
    buildCredits: 4000,
    buildMinerals: 2000,
  },
  {
    chassis: 'Asteroid Mining Outpost',
    role: 'Extracts Minerals from an asteroid field.',
    hull: 1000,
    armor: 0.1,
    shields: 200,
    visionRange: 0.1,
    scanRange: 0.2,
    detectionBase: 10,
    weaponLoadout: [],
    buildCredits: 5000,
    buildMinerals: 1000,
  },
  {
    chassis: 'Gas Giant Refinery',
    role: 'Extracts Fuel from a gas giant.',
    hull: 1000,
    armor: 0.1,
    shields: 200,
    visionRange: 0.1,
    scanRange: 0.2,
    detectionBase: 10,
    weaponLoadout: [],
    buildCredits: 5000,
    buildMinerals: 1000,
  },
  {
    // v3M2.1 — Warp Gate. Cross-system jump node per GALAXY.md §3.
    // v3M18 redesign: hull 1500 -> 1000 (locked 2026-05-02). Auto-mesh
    // replaced with explicit player-authored pairwise connections —
    // see GALAXY.md §3.2 + balance-space-structures.ts. The
    // 'Mesh travel node' label is retained on the chassis role string
    // for back-compat with existing UI copy; semantics are 'Connection
    // hub for authored gate-pairs' under the new design.
    chassis: 'Warp Gate',
    role: 'Connection hub — author one pairwise jump-link to another owned Warp Gate.',
    hull: 1000,
    armor: 0.1,
    shields: 200,
    visionRange: 0.15,
    scanRange: 0.3,
    detectionBase: 20,
    weaponLoadout: [],
    buildCredits: 5000,
    buildMinerals: 2000,
  },
  {
    // v3M2.2 — ODP Ring Platform. Spawned six-up by the
    // advance-odp.ts pass when an `orbital-defence-platform` Structure
    // activates on a planet. Not Constructor-built — never appears in
    // the buildSpaceStructure menu; build cost is paid at the planet
    // structure level. Concussion missile loadout per STRUCTURES §3.4
    // ("auto-armed with the best currently-researched Concussion
    // missile"); chassis stats roughly match a Weapons Platform.
    chassis: 'ODP Platform',
    role: 'Ring platform deployed by an Orbital Defence Platform.',
    hull: 1500,
    armor: 0.15,
    shields: 300,
    visionRange: 0.15,
    scanRange: 0.3,
    detectionBase: 20,
    weaponLoadout: [{ weapon: 'Concussion Missile Weapon', count: 1 }],
    // Build cost is irrelevant because ring platforms are never
    // bought through the Constructor menu — the values are placeholders
    // for save-shape symmetry with the other space-structure templates.
    buildCredits: 0,
    buildMinerals: 0,
  },
  {
    // v3M2.3 — Minefield. Constructor-built depleting hazard terrain
    // per STRUCTURES §3.2. Soft target — small hull pool so escorts
    // can clear unwanted minefields rather than engaging mine-by-mine.
    // Detection scaled lower than other space structures so a
    // minefield doesn't double as a passive sensor — mines should
    // surprise, not scout.
    chassis: 'Minefield',
    role: 'Depleting hazard zone — damages ships passing through.',
    hull: 500,
    armor: 0.05,
    shields: 0,
    visionRange: 0.05,
    scanRange: 0.1,
    detectionBase: 5,
    weaponLoadout: [],
    buildCredits: 1500,
    buildMinerals: 800,
  },
];

export function loadGameData(): LoadedGameData {
  const s = shipsJson as unknown as ShipsJsonShape;
  const t = techJson as unknown as TechJsonShape;
  const h = habitabilityJson as unknown as HabitabilityJsonShape;
  const a = aiBehavioursJson as unknown as AiBehavioursJsonShape;
  const st = structuresJson as unknown as StructuresJsonShape;
  const nm = namesJson as unknown as NamesJsonShape;
  // v3M10.1.0 — balance.json carries a schemaVersion + generatedAt
  // header; the body is structurally identical to BalanceData. Cast
  // through unknown so TS doesn't try to reconcile the embedded JSON
  // type with the runtime shape (it'd choke on the Record<number,
  // number> for ticksByClass which JSON serialises as string keys).
  const bal = balanceJson as unknown as BalanceData & {
    schemaVersion: number;
    generatedAt: string;
  };

  if (s.schemaVersion !== 1) {
    throw new Error(`Unsupported ships.json schemaVersion=${s.schemaVersion}`);
  }
  if (t.schemaVersion !== 1) {
    throw new Error(`Unsupported tech-tree.json schemaVersion=${t.schemaVersion}`);
  }
  if (h.schemaVersion !== 1) {
    throw new Error(`Unsupported habitability.json schemaVersion=${h.schemaVersion}`);
  }
  if (a.schemaVersion !== 1) {
    throw new Error(`Unsupported ai-behaviours.json schemaVersion=${a.schemaVersion}`);
  }
  if (st.schemaVersion !== 4) {
    throw new Error(`Unsupported structures.json schemaVersion=${st.schemaVersion}`);
  }
  if (bal.schemaVersion !== 1) {
    throw new Error(`Unsupported balance.json schemaVersion=${bal.schemaVersion}`);
  }

  const shipTemplates = new Map<string, ShipTemplate>();
  // M14 space structures are synthesized after the JSON load — they're
  // classified as "stationary ships" per STRUCTURES §3, share the
  // ShipTemplate shape with regular chassis, and live in state.ships at
  // runtime. Keeping them out of ships.json keeps the xlsx pipeline
  // focused on authored combat chassis while giving us a single code-
  // level source of truth for space-structure stats.
  injectSpaceStructureTemplates(shipTemplates);

  for (const row of s.ships) {
    const key = `${row.raceId}:${row.chassis}`;
    shipTemplates.set(key, {
      chassis: row.chassis,
      classLevel: row.classLevel as ShipClass,
      raceId: row.raceId as RaceId,
      role: row.role,
      hull: row.hull,
      armor: row.armor,
      shields: row.shields,
      empProtection: row.empProtection,
      sublightSpeed: row.sublightSpeed,
      dodge: row.dodge,
      visionRange: row.visionRange,
      scanRange: row.scanRange,
      detectionBase: row.detectionBase,
      stealthBase: row.stealthBase,
      energyCapacity: row.energyCapacity,
      energyRegen: row.energyRegen,
      // v4M5.2b — three decomposed hyperdrive stats. Legacy
      // `hyperdriveFuelUse` retired alongside this commit.
      hyperdriveSpeed: row.hyperdriveSpeed,
      hyperdriveRange: row.hyperdriveRange,
      hyperdriveFuelUsePerUnit: row.hyperdriveFuelUsePerUnit,
      jumpDurationTicks:
        typeof row.jumpDurationTicks === 'number' && row.jumpDurationTicks > 0
          ? row.jumpDurationTicks
          : JUMP_DURATION_DEFAULT,
      weaponLoadout: row.weaponLoadout,
      embarks: row.embarks,
      popBuildCost: row.popBuildCost,
      creditsBuildCost: row.creditsBuildCost,
      mineralsBuildCost: row.mineralsBuildCost,
      upkeep: row.upkeep,
      canCloak: row.canCloak,
      specialAbilities: row.specialAbilities,
      aura: row.aura ?? null,
    });
  }

  // v3M17.1 — synthetic squadron templates (one per v1 race) injected
  // AFTER the regular ship templates load: squadron stats derive from
  // the parent fighter chassis, which must already be in shipTemplates.
  injectSquadronTemplates(shipTemplates);

  const weaponDefs = new Map<string, WeaponDef>();
  for (const row of s.weapons) {
    // ships.json's category/subType/damageType fields are free-form strings,
    // but the WeaponDef literal unions are locked. Cast at the boundary — any
    // future xlsx drift will surface at first combat resolution rather than
    // at load time, which is acceptable for a single-player v1.
    const category = row.category as WeaponDef['category'];
    const subType = row.subType as WeaponDef['subType'];
    const damageType = row.damageType as WeaponDef['damageType'];
    weaponDefs.set(row.name, {
      name: row.name,
      category,
      subType,
      mk: row.mk,
      rateOfFire: row.rateOfFire,
      range: row.range,
      damageType,
      damage: row.damage,
      accuracy: row.accuracy,
      magazine: row.magazine,
      blastRadius: row.blastRadius,
    });
  }

  const techDefs = new Map<string, TechDef>();
  for (const row of t.techs) {
    // MFix7.2 — older tech-tree.json blobs (pre-v3M10.1.x retune)
    // don't have the parsed fields. Default to null so behaviour
    // falls back to the formula-driven path in research-effects.ts
    // until a regen lands.
    const r = row as typeof row & {
      parsedMultiplier?: number | null;
      parsedAdditive?: number | null;
      parsedRegenPercent?: number | null;
    };
    techDefs.set(row.techName, {
      techName: row.techName,
      category: row.category,
      level: row.level,
      race: row.race,
      prerequisites: row.prerequisites,
      researchCost: row.researchCost,
      researchTimeTicks: row.researchTimeTicks,
      effect: row.effect,
      unlocks: row.unlocks,
      parsedMultiplier: r.parsedMultiplier ?? null,
      parsedAdditive: r.parsedAdditive ?? null,
      parsedRegenPercent: r.parsedRegenPercent ?? null,
    });
  }

  const habitability: HabitabilityData = {
    raceAffinity: h.raceAffinity,
    baseInherent: h.baseInherent,
    techBonus: h.techBonus,
    bands: h.bands,
    sizeModulation: h.sizeModulation,
  };

  const aiBehaviours: AiBehavioursData = {
    behaviours: a.behaviours,
    difficulties: a.difficulties,
    structurePriorityByBehaviour: a.structurePriorityByBehaviour,
    shipPriorityByRaceBehaviour: a.shipPriorityByRaceBehaviour,
    researchPriorityByBehaviour: a.researchPriorityByBehaviour,
    spaceStructurePriorityByBehaviour: a.spaceStructurePriorityByBehaviour,
  };

  const structures: StructuresData = {
    byKind: st.byKind,
    spaceByKind: st.spaceByKind,
    features: st.features,
    autoRepairProportionPerTick: st.autoRepairProportionPerTick,
    baseCredits: st.baseCredits,
    baseMinerals: st.baseMinerals,
    baseFuel: st.baseFuel,
    diplomaticCap: st.diplomaticCap,
  };

  // v3M10.1.0 — balanceData. The JSON carries a header (schemaVersion +
  // generatedAt) we drop here so the runtime shape is exactly BalanceData.
  // Reassemble ticksByClass with numeric keys — JSON keys are strings, but
  // the runtime callers index with the numeric `classLevel`.
  const balance: BalanceData = {
    combat: bal.combat,
    emp: bal.emp,
    bombardment: bal.bombardment,
    detection: bal.detection,
    movement: bal.movement,
    economy: bal.economy,
    colonisation: bal.colonisation,
    terraforming: bal.terraforming,
    build: {
      ticksByClass: Object.fromEntries(
        Object.entries(bal.build.ticksByClass).map(([k, v]) => [Number(k), v as number]),
      ),
      shipSpawnRingRadius: bal.build.shipSpawnRingRadius,
    },
    ai: bal.ai,
    scenario: bal.scenario,
    // v3M21.1 — diplomacy block. The JSON's `defensiveStructures` is
    // shape-compatible with the runtime BiomeKind-keyed Record but
    // typed as plain `Record<string, ...>` from convert-xlsx; cast
    // through `as unknown as BalanceData['diplomacy']['defensiveStructures']`
    // so the runtime preserves type safety without round-tripping the
    // BiomeKind union into convert-xlsx (which has no biome-import).
    diplomacy: {
      ...bal.diplomacy,
      defensiveStructures:
        bal.diplomacy.defensiveStructures as unknown as BalanceData['diplomacy']['defensiveStructures'],
    },
  };

  // MFix8.5 — load Auras catalogue from ships.json (newer regen blobs).
  // Older blobs without the auras array degrade gracefully — every
  // emitter looks up `aura` and finds nothing, so no aura bonuses
  // apply until the user re-runs `npm run data:build`.
  const auras = new Map<string, AuraDef>();
  if (s.auras) {
    for (const row of s.auras) {
      auras.set(row.auraName, {
        name: row.auraName,
        range: row.range,
        shieldMult: row.shieldMult,
        shieldRegenMult: row.shieldRegenMult,
        damageMult: row.damageMult,
        speedMult: row.speedMult,
        stealthBonus: row.stealthBonus,
        detectionBonus: row.detectionBonus,
      });
    }
  }

  // v3M20 — name pools. Cast `readonly` away on the struct shape: NamesData
  // declares `readonly string[]` to communicate intent (consumed once,
  // never mutated), but the JSON-imported arrays are plain mutable
  // arrays that satisfy the readonly contract structurally.
  const names: NamesData = {
    systems: nm.systems,
    planets: nm.planets,
  };

  return {
    shipTemplates,
    auras,
    weapons: s.weapons,
    weaponDefs,
    races: s.races,
    techs: t.techs,
    techDefs,
    habitability,
    aiBehaviours,
    structures,
    balance,
    names,
  };
}
